import { Formik, Form } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { ERROR, PENDING } from '../../../../../../design-system/constants';
import { Loader } from '../../../../../../design-system/Loader';
import { GlobalLoader } from '../../../../../../shared/components/layout/GlobalLoader';
import {
    fetchQuotePricing,
    postChangeQuotePricesRequest,
} from '../../../../../../resources/contract/actions';
import {
    pricingValidationSchema,
    calculatePricePerPax,
} from '../../../../../../resources/contract/operations';
import {
    areProofOfDocumentsLoading as areProofOfDocumentsLoadingSelector,
    getInitialQuotePricing,
    getQuotePricingAPICallStatus,
    getChangeQuotePricesStatus,
    getAllValidProofOfDocuments,
} from '../../../../../../resources/contract/selectors';
import {
    ContractEditStep,
    ContractId,
    FilledQuotePricing,
} from '../../../../../../resources/contract/types';
import { AllTranslationKeys } from '../../../../../../shared/intl/types';
import { useAppDispatch, useAppSelector } from '../../../../../../shared/redux';
import { SidePanelViewMode } from '../../../../SidePanel/types';
import { ErrorOverlay } from '../../../../../Pipeline/components/Error';
import styleContract from '../../../Contract.module.css';
import contractFormStyle from '../ContractFormElements.module.css';
import { NumberFormInput } from '../NumberFormInput';
import { SelectCurrency } from '../SelectCurrency';
import { StepButtons } from '../StepButtons';

import s from './PricingStep.module.css';
import { UploadProofOfDocuments } from './UploadProofOfDocuments';

type Props = {
    contractId: ContractId;
    numberOfTravelers: number;
    viewMode?: SidePanelViewMode;
};

export const PricingStep = ({ contractId, numberOfTravelers, viewMode }: Props): JSX.Element => {
    const dispatch = useAppDispatch();
    const status = useAppSelector(getQuotePricingAPICallStatus);
    const initialQuotePricing = useAppSelector(getInitialQuotePricing(numberOfTravelers));
    const areProofOfDocumentsLoading = useAppSelector(areProofOfDocumentsLoadingSelector);
    const postChangeQuotePricesStatus = useAppSelector(getChangeQuotePricesStatus);
    const proofOfDocuments = useAppSelector(getAllValidProofOfDocuments);

    const [isMissingProofOfDocuments, setIsMissingProofOfDocuments] = useState(false);

    useEffect(() => {
        dispatch(fetchQuotePricing(contractId));
    }, [contractId, dispatch]);

    if (status === PENDING) {
        return (
            <div className={styleContract.loader}>
                <Loader />
                <h2 className={styleContract.loading} data-testid="pricing-loader">
                    <GlobalLoader />
                </h2>
            </div>
        );
    }

    if (status === ERROR) {
        return (
            <div className={styleContract.contractContainer}>
                <ErrorOverlay onRefresh={() => fetchQuotePricing(contractId)} />
            </div>
        );
    }

    return (
        <div className={s.pricingStep} {...(viewMode && { 'data-contract-view': viewMode })}>
            <p className={styleContract.stepSubtitle}>
                <FormattedMessage
                    id="contract.step_subtitle"
                    values={{
                        step: ContractEditStep.PRICING,
                    }}
                />
            </p>
            <h1 className="title-m">
                <FormattedMessage id="contract.pricing.title" />
            </h1>
            <p className={s.headerDescription}>
                <FormattedMessage id="contract.pricing.description" />
            </p>
            <Formik
                initialValues={initialQuotePricing}
                validationSchema={pricingValidationSchema}
                enableReinitialize
                onSubmit={(values) => {
                    if (values.currency === undefined) {
                        return;
                    }
                    if (values.internationalFlightsPrice > 0 && proofOfDocuments.length === 0) {
                        return setIsMissingProofOfDocuments(true);
                    }

                    dispatch(
                        postChangeQuotePricesRequest(contractId, values as FilledQuotePricing),
                    );
                }}
            >
                {({ isValid, values, setFieldValue, errors }) => (
                    <Form className={s.quotePricingForm}>
                        <SelectCurrency
                            wrapperClassName={s.selectCurrencyContainer}
                            name="currency"
                            inputId="currency"
                            labelTranslationId="contract.pricing.currency"
                            disabled={initialQuotePricing.currency !== undefined}
                            viewMode={viewMode}
                        />
                        <NumberFormInput
                            name="totalPrice"
                            label="contract.pricing.total.label"
                            description="contract.pricing.total.description"
                            currency={values.currency}
                            containerClassName={s.selectCurrencyContainer}
                            viewMode={viewMode}
                            onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                                const flights =
                                    values.domesticFlightsPrice + values.internationalFlightsPrice;
                                void setFieldValue('totalPrice', +value, true);
                                void setFieldValue('landServicesPrice', +value - flights);
                                void setFieldValue(
                                    'pricePerPax',
                                    calculatePricePerPax(+value, numberOfTravelers),
                                );
                            }}
                        />
                        <div className={contractFormStyle.inputContainer}>
                            <div
                                className={contractFormStyle.labelContainer}
                                {...(viewMode && { 'data-contract-view': viewMode })}
                            >
                                <label>
                                    <FormattedMessage id="contract.pricing.price_pax.label" />
                                </label>
                                <p className={contractFormStyle.inputDescription}>
                                    <FormattedMessage id="contract.pricing.price_pax.description" />
                                </p>
                            </div>
                            <p data-testid="contract-price-per-pax">
                                <FormattedNumber
                                    value={values.pricePerPax}
                                    currency={values.currency || 'EUR'}
                                    style="currency"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                            </p>
                        </div>
                        <NumberFormInput
                            name="internationalFlightsPrice"
                            label="contract.pricing.international_flights"
                            containerClassName={s.selectCurrencyContainer}
                            currency={values.currency}
                            viewMode={viewMode}
                            onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                                void setFieldValue('internationalFlightsPrice', +value);
                                void setFieldValue(
                                    'landServicesPrice',
                                    values.totalPrice - (values.domesticFlightsPrice + +value),
                                );
                            }}
                        />
                        <NumberFormInput
                            name="domesticFlightsPrice"
                            label="contract.pricing.domestic_flights"
                            containerClassName={s.selectCurrencyContainer}
                            currency={values.currency}
                            viewMode={viewMode}
                            onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                                void setFieldValue('domesticFlightsPrice', +value);
                                void setFieldValue(
                                    'landServicesPrice',
                                    values.totalPrice - (+value + values.internationalFlightsPrice),
                                );
                            }}
                        />
                        <div className={contractFormStyle.inputContainer}>
                            <div
                                className={contractFormStyle.labelContainer}
                                {...(viewMode && { 'data-contract-view': viewMode })}
                            >
                                <label>
                                    <FormattedMessage id="contract.pricing.land_services.label" />
                                </label>
                                <p className={contractFormStyle.inputDescription}>
                                    <FormattedMessage id="contract.pricing.land_services.description" />
                                </p>
                            </div>
                            <div className={s.landServicesPriceContainer}>
                                <p data-testid="contract-land-services-price">
                                    <FormattedNumber
                                        value={values.landServicesPrice}
                                        style="currency"
                                        maximumFractionDigits={2}
                                        minimumFractionDigits={0}
                                        currency={values.currency || 'EUR'}
                                    />
                                </p>
                                {errors.landServicesPrice && (
                                    <p className={'annotation red'}>
                                        <FormattedMessage
                                            id={errors.landServicesPrice as AllTranslationKeys}
                                        />
                                    </p>
                                )}
                            </div>
                        </div>
                        {values.internationalFlightsPrice > 0 && (
                            <UploadProofOfDocuments
                                contractId={contractId}
                                isMissingProofOfDocuments={isMissingProofOfDocuments}
                                viewMode={viewMode}
                            />
                        )}
                        <div className={s.summaryContainer}>
                            <p>
                                <FormattedMessage
                                    id="contract.pricing.sum_up"
                                    values={{
                                        amount: <strong>{values.totalPrice}</strong>,
                                        currency: <strong>{values?.currency || 'EUR'}</strong>,
                                    }}
                                />
                            </p>
                        </div>
                        <StepButtons
                            isLoading={postChangeQuotePricesStatus === PENDING}
                            isNextStepDisabled={areProofOfDocumentsLoading || !isValid}
                            dataTestId="button-submit-step-3"
                            viewMode={viewMode}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
};
